/**
 * Format start_time / end_time into "ДД.ММ.ГГГГ ЧЧ:ММ UTC".
 *
 * Handles two formats:
 * - Full ISO: "2026-03-16T13:20:00.000Z" → parse date+time from value
 * - Short HH:MM: "13:20" → use fallbackDate for the date part
 */
export type FormattedInterval = {
  line1: string;
  line2?: string;
};

/**
 * Format start_time + end_time interval for DataGrid column.
 *
 * Scenarios:
 * A) Same UTC day → "02.04.2026" / "10:00 – 12:30 UTC"
 * B) Different UTC days → "02.04.2026, 10:00 UTC" / "03.04.2026, 10:00 UTC"
 * C) Only one valid → single "DD.MM.YYYY, HH:MM UTC" line
 * D) Short HH:MM → uses fallbackDate for date part
 * E) Nothing valid → fallbackDate as date or "—"
 *
 * Never returns "Invalid Date".
 */
export function formatIntervalForGrid(
  startTime?: string | null,
  endTime?: string | null,
  fallbackDate?: string | null
): FormattedInterval {
  const parse = (raw: string | undefined | null): Date | null => {
    if (!raw) return null;
    if (raw.includes('T') || raw.length > 10) {
      const d = new Date(raw);
      return isNaN(d.getTime()) ? null : d;
    }
    // Short HH:MM — combine with fallbackDate
    const m = /^(\d{1,2}):(\d{2})$/.exec(raw.trim());
    if (m && fallbackDate) {
      const base = new Date(fallbackDate);
      if (!isNaN(base.getTime())) {
        base.setUTCHours(parseInt(m[1], 10), parseInt(m[2], 10), 0, 0);
        return base;
      }
    }
    return null;
  };

  const fmtDate = (d: Date) =>
    d.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    });
  const fmtTime = (d: Date) =>
    d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
  const fmtFull = (d: Date) => `${fmtDate(d)}, ${fmtTime(d)} UTC`;

  const sameUtcDay = (a: Date, b: Date) =>
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate();

  const s = parse(startTime);
  const e = parse(endTime);

  if (s && e) {
    if (sameUtcDay(s, e)) {
      return { line1: fmtDate(s), line2: `${fmtTime(s)} – ${fmtTime(e)} UTC` };
    }
    return { line1: fmtFull(s), line2: fmtFull(e) };
  }

  if (s) return { line1: fmtFull(s) };
  if (e) return { line1: fmtFull(e) };

  // Fallback to date only
  if (fallbackDate) {
    const fb = new Date(fallbackDate);
    if (!isNaN(fb.getTime())) return { line1: fmtDate(fb) };
  }

  return { line1: '—' };
}

export function formatTimeWithDate(
  timeValue: string | undefined | null,
  fallbackDate: string | undefined | null
): string {
  if (!timeValue) return '—';

  // Full ISO datetime (contains 'T' or is long enough)
  if (timeValue.includes('T') || timeValue.length > 10) {
    const d = new Date(timeValue);
    if (!isNaN(d.getTime())) {
      return (
        d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
        ' ' +
        d.toLocaleTimeString('ru-RU', {
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'UTC',
        }) +
        ' UTC'
      );
    }
  }

  // Short HH:MM format — prepend date from fallbackDate
  const datePart = fallbackDate ? new Date(fallbackDate) : null;
  const dateStr =
    datePart && !isNaN(datePart.getTime())
      ? datePart.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : '';

  return dateStr ? `${dateStr} ${timeValue} UTC` : `${timeValue} UTC`;
}
